import React, { useState, useRef, ChangeEvent, DragEvent } from "react";
import { Form, Image, Alert, Spinner } from "react-bootstrap";
import { FaUpload, FaTimes } from "react-icons/fa";
import { Icon } from "../common/Icon";
import apiClient from "../../api/apiClient";
import { resolveLogoUrl } from "../../utils/urlUtils";

interface LogoUploadFieldProps {
    companyId?: number;
    value: string;
    onChange: (url: string) => void;
    onLogoUrlChange?: (logoUrl: string) => void; // For storing actual logo_url to DB (not data URL)
    onTempPathChange?: (tempPath: string) => void; // Called when temp file is uploaded
    allowTempUpload?: boolean; // Allow uploads without company ID
    label?: string;
    helperText?: string;
    hideUrlInput?: boolean; // Hide the URL input field (useful when URL input is handled elsewhere)
}

const LogoUploadField: React.FC<LogoUploadFieldProps> = ({
    companyId,
    value,
    onChange,
    onLogoUrlChange,
    onTempPathChange,
    allowTempUpload = false,
    label = "Company Logo",
    helperText,
    hideUrlInput = false,
}) => {
    const [uploadProgress, setUploadProgress] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = async (file: File) => {
        // Determine if we should use temp upload
        const useTempUpload = !companyId && allowTempUpload;

        if (!companyId && !allowTempUpload) {
            setUploadError("Company must be saved before uploading a logo file");
            return;
        }

        // Validate file type
        const allowedTypes = ["image/jpeg", "image/png", "image/svg+xml", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
            setUploadError("Invalid file type. Please upload JPG, PNG, SVG, or WEBP images.");
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setUploadError("File size exceeds 5MB");
            return;
        }

        setUploadError(null);
        setUploadProgress(true);

        try {
            const formData = new FormData();
            formData.append("file", file);

            const endpoint = useTempUpload
                ? "/companies/logo/upload-temp"
                : `/companies/${companyId}/logo/upload`;

            const response = await apiClient.post(endpoint, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (useTempUpload) {
                // For temp uploads, we get temp_path and data_url for preview
                if (onTempPathChange) {
                    onTempPathChange(response.temp_path);
                }
                // Use data URL for preview (base64 encoded)
                if (response.data_url) {
                    onChange(response.data_url);
                }
            } else {
                // For regular uploads:
                // - Store logo_url in a separate state for DB (via onLogoUrlChange)
                // - Use data_url for immediate preview (via onChange)
                // Only store logo_url if it's not a base64 data URL
                if (response.logo_url && !response.logo_url.startsWith("data:")) {
                    if (onLogoUrlChange) {
                        onLogoUrlChange(response.logo_url);
                    }
                }
                // Use data_url for immediate preview, fallback to logo_url if data_url not available
                if (response.data_url) {
                    onChange(response.data_url);
                } else if (response.logo_url && !response.logo_url.startsWith("data:")) {
                    onChange(response.logo_url);
                }
            }
        } catch (error: any) {
            setUploadError(error.response?.data?.detail || "Failed to upload logo");
        } finally {
            setUploadProgress(false);
        }
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleClear = () => {
        onChange("");
        // Also clear the actual logo URL if callback exists
        if (onLogoUrlChange) {
            onLogoUrlChange("");
        }
        setUploadError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <div className="logo-upload-field">
            {label && <Form.Label className="form-label-custom mb-2">{label}</Form.Label>}

            {/* URL Input - Only show if not hidden */}
            {!hideUrlInput && (
                <>
                    <Form.Control
                        type="url"
                        value={value && value.startsWith("data:") ? "" : value}
                        onChange={(e) => {
                            const url = e.target.value;
                            // If user enters a URL (not data URL), clear actualLogoUrl
                            // so form submission uses the entered URL
                            if (onLogoUrlChange && url && !url.startsWith("data:")) {
                                onLogoUrlChange("");
                            }
                            onChange(url);
                        }}
                        placeholder="https://example.com/logo.png"
                        className="form-control-modern mb-2"
                    />

                    {/* File Upload Option */}
                    <div className="text-center my-3">
                        <small className="text-muted">— or —</small>
                    </div>
                </>
            )}

            <div
                className={`file-upload-area ${isDragging ? "dragging" : ""} ${!companyId && !allowTempUpload ? "disabled" : ""}`}
                onDragOver={companyId || allowTempUpload ? handleDragOver : undefined}
                onDragLeave={companyId || allowTempUpload ? handleDragLeave : undefined}
                onDrop={companyId || allowTempUpload ? handleDrop : undefined}
                onClick={companyId || allowTempUpload ? () => fileInputRef.current?.click() : undefined}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/svg+xml,image/webp"
                    onChange={handleFileChange}
                    disabled={!companyId && !allowTempUpload}
                    style={{ display: "none" }}
                />
                {uploadProgress ? (
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" className="mb-2" />
                        <div className="small">Uploading...</div>
                    </div>
                ) : (
                    <div className="text-center py-3">
                        <Icon icon={FaUpload} size="24px" className="mb-2 text-muted" />
                        <div className="small fw-bold">
                            {companyId || allowTempUpload ? "Click or drag to upload file" : "Save company first to upload files"}
                        </div>
                        {(companyId || allowTempUpload) && <small className="text-muted d-block">JPG, PNG, SVG, WEBP (max 5MB)</small>}
                    </div>
                )}
            </div>

            {/* Error Display */}
            {uploadError && (
                <Alert variant="danger" className="mt-2 py-2 px-3 small mb-0">
                    {uploadError}
                </Alert>
            )}

            {/* Helper Text */}
            {helperText && !uploadError && (
                <Form.Text className="text-muted d-block mt-2">{helperText}</Form.Text>
            )}

            {/* Preview */}
            {value && (
                <div className="logo-preview mt-3 p-3 border rounded bg-light">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                            <Image
                                src={resolveLogoUrl(value)}
                                alt="Company Logo"
                                thumbnail
                                style={{ maxWidth: "80px", maxHeight: "80px", objectFit: "contain" }}
                                onError={(e) => {
                                    // Fallback if image fails to load
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                }}
                            />
                            <small className="text-muted">Current logo</small>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={handleClear}
                        >
                            <Icon icon={FaTimes} className="me-1" />
                            Remove
                        </button>
                    </div>
                </div>
            )}

            <style>{`
        .file-upload-area {
          border: 2px dashed #dee2e6;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          background-color: #f8f9fa;
        }

        .file-upload-area:not(.disabled):hover,
        .file-upload-area.dragging {
          border-color: #007bff;
          background-color: rgba(0, 123, 255, 0.05);
        }

        .file-upload-area.disabled {
          opacity: 0.6;
          cursor: not-allowed;
          background-color: #e9ecef;
        }
      `}</style>
        </div>
    );
};

export default LogoUploadField;
